import {
  Timestamp,
  Transaction,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  where,
} from "firebase/firestore/lite";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import {
  FirestoreMenuDocument,
  FirestoreMenuSection,
  FirestoreRestaurantMenuDocument,
  Menu,
  MenuSection,
  RestaurantMenu,
} from "../../types/menu";
import { FetchMenusResult } from "../reducers/menuReducer";

export interface MenuApi {
  fetchMenus(restaurantUuid: string): Promise<FetchMenusResult>;

  // Sets a menu, optionally allowing the caller to set the menu sections as well.
  // Menu sections are optional because they are not always needed when modifying an attribute of a menu.
  setMenu(
    menu: Menu,
    creatingNew: boolean,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionsOfMenu?: MenuSection[]
  ): Promise<void>;
  deleteMenu(
    restaurantUuid: string,
    menuId: string,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionIdsToDelete: string[]
  ): Promise<void>;

  setMenuSection(menuSection: MenuSection): Promise<void>;
  deleteMenuSection(
    restaurantUuid: string,
    menuId: string,
    menuSectionId: string
  ): Promise<void>;

  setMenuDisplayOrder(
    restaurantUuid: string,
    menuIdsInDisplayOrder: string[]
  ): Promise<void>;
}

/**
 * An implementation of the MenuApi that uses Firestore as the backend.
 */
export class FirestoreBackedMenuApi implements MenuApi {
  private firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  public fetchMenus(restaurantUuid: string): Promise<FetchMenusResult> {
    return Promise.all([
      this.fetchAllMenus(restaurantUuid),
      this.fetchAllMenuSections(restaurantUuid),
      this.fetchRestaurantMenu(restaurantUuid),
    ]).then(([menus, menuSections, restaurantMenu]) => {
      return {
        menus,
        menuSections,
        menuIdsInDisplayOrder: restaurantMenu.menuIdsInDisplayOrder,
      } as FetchMenusResult;
    });
  }

  private fetchAllMenus(restaurantUuid: string): Promise<Menu[]> {
    return getDocs(query(this.firestoreRefs.menus(restaurantUuid))).then(
      (querySnapshot) => {
        return querySnapshot.docs.map((doc) => {
          const data = doc.data() as FirestoreMenuDocument;
          // drop the fields that are not part of the Menu type
          const {
            restaurantRef,
            menuSectionRefsInDisplayOrder,
            createdAt,
            modifiedAt,
            ...menu
          } = data;
          return {
            ...menu,
            createdAtMillis: createdAt ? createdAt.toMillis() : null,
            modifiedAtMillis: modifiedAt ? modifiedAt.toMillis() : null,
          };
        });
      }
    );
  }

  private fetchAllMenuSections(restaurantUuid: string): Promise<MenuSection[]> {
    return getDocs(query(this.firestoreRefs.menuSections(restaurantUuid))).then(
      (querySnapshot) => {
        return querySnapshot.docs.map((doc) => {
          const data = doc.data() as FirestoreMenuSection;
          // drop the fields that are not part of the MenuSection type
          const {
            restaurantRef,
            menuRef,
            dishRefsInDisplayOrder,
            ...menuSection
          } = data;
          return menuSection as MenuSection;
        });
      }
    );
  }

  private fetchRestaurantMenu(restaurantUuid: string): Promise<RestaurantMenu> {
    return getDoc(this.firestoreRefs.restaurantMenu(restaurantUuid)).then(
      (doc) => {
        if (!doc.exists()) {
          // No menus yet.
          return {
            menuIdsInDisplayOrder: [],
          } as RestaurantMenu;
        }
        const data = doc.data() as FirestoreRestaurantMenuDocument;
        // drop the fields that are not part of the RestaurantMenu type
        const { menuRefsInDisplayOrder, ...restaurantMenu } = data;
        return restaurantMenu as RestaurantMenu;
      }
    );
  }

  // FIXME - make this a transactional api call to the BE.
  public setMenu(
    menu: Menu,
    creatingNew: boolean,
    previousMenuIdsInDisplayOrder: string[],
    menuSections?: MenuSection[]
  ): Promise<void> {
    if (creatingNew) {
      const newMenuIdsInDisplayOrder = [
        menu.id,
        ...previousMenuIdsInDisplayOrder,
      ];
      const withMetaFields: Menu = {
        ...menu,
        createdAtMillis: Date.now(),
        modifiedAtMillis: Date.now(),
      };

      return this.setMenuDisplayOrder(
        menu.restaurantUuid,
        newMenuIdsInDisplayOrder
      ).then(() => {
        return this.setMenu(
          withMetaFields,
          false,
          previousMenuIdsInDisplayOrder,
          menuSections
        );
      });
    }

    const { createdAtMillis, modifiedAtMillis, ...menuRest } = menu;
    const additionalFields = {
      restaurantRef: this.firestoreRefs.restaurant(menu.restaurantUuid),
      // Always recompute the menuSectionRefsInDisplayOrder field
      menuSectionRefsInDisplayOrder: menu.menuSectionIdsInDisplayOrder.map(
        (menuSectionId) =>
          this.firestoreRefs.menuSection(menu.restaurantUuid, menuSectionId)
      ),
    };
    const firestoreMenu = {
      ...menuRest,
      ...additionalFields,
      createdAt: createdAtMillis ? Timestamp.fromMillis(createdAtMillis) : null,
      modifiedAt: Timestamp.now(),
    } as FirestoreMenuDocument;

    // If menu sections are provided, set them as well
    // but if they are not provided, do not touch the menu sections
    // FIXME - At this point if some menu sections were deleted, they will still be in the menu
    // this happens when the menu sections are deleted from the EditMenuDrawer.
    // The menuSection documents remain, but references are deleted from the menu document.
    const menuSectionPromises = menuSections
      ? menuSections.map((menuSection) => this.setMenuSection(menuSection))
      : [];

    // FIXME - make this an api call to the backend & use firebase transactions
    return Promise.all(menuSectionPromises).then(() => {
      return setDoc(
        this.firestoreRefs.menu(menu.restaurantUuid, menu.id),
        firestoreMenu,
        {
          merge: true,
        }
      );
    });
  }

  public async deleteMenu(
    restaurantUuid: string,
    menuId: string,
    previousMenuIdsInDisplayOrder: string[],
    menuSectionIdsToDelete: string[]
  ): Promise<void> {
    // remove the menu from the restaurant menu
    const newMenuIdsInDisplayOrder = previousMenuIdsInDisplayOrder.filter(
      (id) => id !== menuId
    );
    const updateRestaurantMenu = this.setMenuDisplayOrder(
      restaurantUuid,
      newMenuIdsInDisplayOrder
    );

    // remove the menuSecions
    const deleteMenuSections = await Promise.all(
      menuSectionIdsToDelete.map((menuSectionId) =>
        deleteDoc(this.firestoreRefs.menuSection(restaurantUuid, menuSectionId))
      )
    );

    // delete the menu
    const deleteMenu = await deleteDoc(
      this.firestoreRefs.menu(restaurantUuid, menuId)
    );
  }

  public setMenuSection(menuSection: MenuSection): Promise<void> {
    const additionalFields = {
      restaurantRef: this.firestoreRefs.restaurant(menuSection.restaurantUuid),
      menuRef: this.firestoreRefs.menu(
        menuSection.restaurantUuid,
        menuSection.menuId
      ),
      dishRefsInDisplayOrder: menuSection.dishIdsInDisplayOrder.map((dishId) =>
        this.firestoreRefs.dish(menuSection.restaurantUuid, dishId)
      ),
    };
    const firestoreMenuSection = {
      ...menuSection,
      ...additionalFields,
    } as FirestoreMenuSection;

    return setDoc(
      this.firestoreRefs.menuSection(
        menuSection.restaurantUuid,
        menuSection.id
      ),
      firestoreMenuSection,
      {
        merge: true,
      }
    ).then(() => {
      this.touchMenu(menuSection.restaurantUuid, menuSection.menuId);
    });
  }

  /**
   * Removes the given dish id from all menu sections that contain it.
   * in the given firestore transaction.
   *
   * in the given transaction, read all MenuSections that contain this dish id.
   * then update all menusections removing this dish id.
   * deleting the dish itself is not done here, and is left to the caller.
   *
   * @param transaction
   * @param restaurantUuid
   * @param dishId
   * @returns
   */
  public deleteDishFromMenuSections(
    transaction: Transaction,
    restaurantUuid: string,
    dishId: string
  ): Promise<void> {
    // FIXME - getDocs isn't in transaction scope here, but it should be.
    return getDocs(
      query(
        this.firestoreRefs.menuSections(restaurantUuid),
        where("dishIdsInDisplayOrder", "array-contains", dishId)
      )
    ).then((querySnapshot) => {
      querySnapshot.docs.forEach((doc) => {
        const data = doc.data() as FirestoreMenuSection;
        const { dishIdsInDisplayOrder, menuId } = data;
        const newdishIdsInDisplayOrder = dishIdsInDisplayOrder.filter(
          (id: string) => id !== dishId
        );
        transaction.update(doc.ref, {
          dishIdsInDisplayOrder: newdishIdsInDisplayOrder,
          dishRefsInDisplayOrder: newdishIdsInDisplayOrder.map((dishId) =>
            this.firestoreRefs.dish(restaurantUuid, dishId)
          ),
        });

        this.touchMenu(restaurantUuid, menuId, transaction);
      });
    });
  }

  deleteMenuSection(
    restaurantUuid: string,
    menuId: string,
    menuSectionId: string
  ): Promise<void> {
    // FIXME. this would be moved to the backend api, once we have auth ready.

    // update menu and remove the menuSectionId
    // Use a firestore transaction to read the MenuDoc,
    // remove the given menuSectionId. write the updated menuDoc, and
    // then delete the menuSectionDoc.

    return runTransaction(
      this.firestoreRefs.firestore,
      async (transaction: Transaction) => {
        const menuDocRef = this.firestoreRefs.menu(restaurantUuid, menuId);
        const menuDoc = await transaction.get(menuDocRef);
        if (!menuDoc.exists()) {
          throw new Error("Menu does not exist");
        }
        const menuData = menuDoc.data() as FirestoreMenuDocument;
        const updatedMenuSectionIdsInDisplayOrder =
          menuData.menuSectionIdsInDisplayOrder.filter(
            (id) => id !== menuSectionId
          );
        transaction.update(menuDocRef, {
          menuSectionIdsInDisplayOrder: updatedMenuSectionIdsInDisplayOrder,
          menuSectionRefsInDisplayOrder:
            updatedMenuSectionIdsInDisplayOrder.map((id) =>
              this.firestoreRefs.menuSection(restaurantUuid, id)
            ),
        });

        const menuSectionDocRef = this.firestoreRefs.menuSection(
          restaurantUuid,
          menuSectionId
        );

        transaction.delete(menuSectionDocRef);
        this.touchMenu(restaurantUuid, menuId, transaction);
      }
    );
  }

  setMenuDisplayOrder(
    restaurantUuid: string,
    menuIdsInDisplayOrder: string[]
  ): Promise<void> {
    const firestoreRestaurantMenuDoc = {
      menuIdsInDisplayOrder: menuIdsInDisplayOrder,
      menuRefsInDisplayOrder: menuIdsInDisplayOrder.map((menuId) =>
        this.firestoreRefs.menu(restaurantUuid, menuId)
      ),
    } as FirestoreRestaurantMenuDocument;

    return setDoc(
      this.firestoreRefs.restaurantMenu(restaurantUuid),
      firestoreRestaurantMenuDoc,
      {
        merge: true,
      }
    );
  }

  private touchMenu(
    restaurantUuid: string,
    menuId: string,
    transaction?: Transaction
  ): Promise<void> | Transaction {
    if (transaction) {
      return transaction.update(
        this.firestoreRefs.menu(restaurantUuid, menuId),
        {
          modifiedAt: Timestamp.now(),
        }
      );
    }
    return setDoc(
      this.firestoreRefs.menu(restaurantUuid, menuId),
      {
        modifiedAt: Timestamp.now(),
      },
      {
        merge: true,
      }
    );
  }
}
